import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import client, { startAutoRefresh, stopAutoRefresh } from '../api/client';
import {
  saveSession,
  saveUser,
  getUser,
  getUserId,
  clearSession,
  hasSession,
} from '../storage/tokens';

// Auth state for the v2 app. Role is ALWAYS server-provided via GET /api/me
// ({ id, name, email, isAdmin }); the client never decodes the JWT.
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Pull the normalized identity + role from the server (authoritative).
  const fetchMe = useCallback(async () => {
    const { data } = await client.get('/me');
    setUser(data);
    saveUser(data);
    return data;
  }, []);

  // On mount: hydrate optimistically from storage, then confirm with /api/me.
  useEffect(() => {
    let active = true;

    if (!hasSession()) {
      setLoading(false);
      return undefined;
    }

    const cached = getUser();
    if (cached) setUser(cached);

    (async () => {
      try {
        await fetchMe();
        startAutoRefresh();
      } catch {
        // token invalid and refresh failed -> drop the stale session
        clearSession();
        if (active) setUser(null);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [fetchMe]);

  const login = useCallback(
    async (email, password) => {
      const { data } = await client.post('/auth/login', { email, password });
      saveSession(data); // { tokens, user }
      const me = await fetchMe(); // upgrade to full identity incl. isAdmin
      startAutoRefresh();
      return me;
    },
    [fetchMe]
  );

  // Guests only. Role is assigned server-side by email membership — we never
  // send a role field. Timezone from the browser, matching the server contract.
  const register = useCallback(async ({ fName, lName, email, phone, password, password2 }) => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { data } = await client.post('/auth/register', {
      fName,
      lName,
      email,
      phone,
      password,
      password2,
      timezone,
    });
    return data;
  }, []);

  const logout = useCallback(async () => {
    const userID = getUserId();
    try {
      if (userID) await client.post('/auth/logout', { userID });
    } catch {
      // best-effort revoke; clear locally regardless
    }
    stopAutoRefresh();
    clearSession();
    setUser(null);
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    const { data } = await client.post('/auth/requestPasswordReset', { email });
    return data; // may include devResetToken in non-prod
  }, []);

  const resetPassword = useCallback(async (token, password, password2) => {
    const { data } = await client.post('/auth/updatePassword', { token, password, password2 });
    return data;
  }, []);

  const verifyEmail = useCallback(async (token) => {
    const { data } = await client.post('/auth/verifyEmail', { token });
    return data;
  }, []);

  const resendVerification = useCallback(async (email) => {
    const { data } = await client.post('/auth/resendVerification', { email });
    return data; // may include devVerificationToken in non-prod
  }, []);

  // Self-service profile edit (name/phone; email is not editable here).
  // Re-syncs `user` from /api/me so the header greeting etc. pick up the change.
  const updateProfile = useCallback(
    async ({ fName, lName, phone }) => {
      await client.put('/me', { fName, lName, phone });
      return fetchMe();
    },
    [fetchMe]
  );

  // Self-service password change — no email verification, the current
  // password proves identity. The server revokes every other session and
  // returns a fresh token pair for this one, mirroring login()'s upgrade dance.
  const changePassword = useCallback(
    async (currentPassword, password, password2) => {
      const { data } = await client.post('/me/password', { currentPassword, password, password2 });
      saveSession(data);
      await fetchMe();
      return data;
    },
    [fetchMe]
  );

  const value = useMemo(
    () => ({
      user,
      isAdmin: !!user?.isAdmin,
      isInternal: !!user?.isInternal,
      loading,
      login,
      logout,
      register,
      requestPasswordReset,
      resetPassword,
      verifyEmail,
      resendVerification,
      updateProfile,
      changePassword,
    }),
    [
      user,
      loading,
      login,
      logout,
      register,
      requestPasswordReset,
      resetPassword,
      verifyEmail,
      resendVerification,
      updateProfile,
      changePassword,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
